import { useSyncExternalStore } from 'react'

// Global force-update state. Lives outside React because the TanStack Query
// global error callbacks (QueryCache/MutationCache.onError) run outside the
// component tree and need to flip this when the API reports CLIENT_OUTDATED.

export type ForceUpdateState = {
  required: boolean
  minVersion?: string
  clientVersion?: string
}

let state: ForceUpdateState = { required: false }
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

export const forceUpdateStore = {
  /** Enter the blocking "update required" state. Idempotent — first call wins. */
  trigger(data?: { minVersion?: string; clientVersion?: string }) {
    if (state.required) return
    state = {
      required: true,
      minVersion: data?.minVersion,
      clientVersion: data?.clientVersion,
    }
    emit()
  },
  getSnapshot(): ForceUpdateState {
    return state
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

/** True when an oRPC error carries the API's force-update code. */
export function isClientOutdatedError(
  error: unknown
): error is { code: 'CLIENT_OUTDATED'; data?: { minVersion?: string; clientVersion?: string } } {
  return (error as { code?: string } | null)?.code === 'CLIENT_OUTDATED'
}

/** Subscribe a component to the force-update state. */
export function useForceUpdate(): ForceUpdateState {
  return useSyncExternalStore(forceUpdateStore.subscribe, forceUpdateStore.getSnapshot)
}
